import makeErrorFromUnknown from '@/functions/makeErrorFromUnknown';
import type { LoginFormValues } from '@/modules/auth/types/login-form-values';
import config from '@/modules/config/functions/config';
import {
  AuthLoginFetchError,
  AuthLoginGeneralError,
  AuthLoginInternalServerError,
  AuthLoginInvalidCredentialsError,
  AuthLoginRequestError,
  AuthLoginServerConnectionError,
  BaseError,
} from '@/modules/errors/constants/error-class-map.gen';
import logger from '@/modules/logger/constants/logger';
import attachCommonWretchErrorHandlers from '@/modules/query-client/functions/attachCommonWretchErrorHandlers';
import createCommonWretchErrorHandlerMap from '@/modules/query-client/functions/createCommonWretchErrorHandlerMap';
import handleWretchFetchError from '@/modules/query-client/functions/handleWretchFetchError';
import usePublicWretch from '@/modules/query-client/hooks/usePublicWretch';
import { useMutation } from '@tanstack/react-query';

export default function useLoginUserMutation() {
  const httpClient = usePublicWretch();

  return useMutation({
    mutationKey: ['user', 'login'],
    mutationFn: async (data: LoginFormValues) => {
      return attachCommonWretchErrorHandlers(
        httpClient
          .url('api/v1/login')
          .json({
            username: data.username,
            password: data.password,
          })
          .post(),
        createCommonWretchErrorHandlerMap([
          ['badRequest', AuthLoginRequestError],
          ['unauthorized', AuthLoginInvalidCredentialsError],
          ['timeout', AuthLoginServerConnectionError],
          ['internalError', AuthLoginInternalServerError],
          [502, AuthLoginServerConnectionError],
          [503, AuthLoginServerConnectionError],
          [504, AuthLoginServerConnectionError],
          ['fetchError', AuthLoginFetchError],
        ])
      )
        .json<{
          accessToken: string;
          accessTokenExpiresIn: number;
          refreshToken?: string;
          refreshTokenExpiresIn?: number;
        }>()
        .then((data) => data)
        .catch((error) => {
          handleWretchFetchError(error, 'auth.login', AuthLoginFetchError);
          if (error instanceof AuthLoginInvalidCredentialsError) {
            logger.error('auth.login', error, undefined, false);
            throw error;
          } else if (error instanceof BaseError) {
            logger.error('auth.login', error);
            throw error;
          } else {
            logger.error('auth.login', error);
            throw new AuthLoginGeneralError(makeErrorFromUnknown(error));
          }
        });
    },
    retry(failureCount, error) {
      if (error instanceof AuthLoginInvalidCredentialsError) return false;
      return failureCount < config('misc.defaultMutationRetryCount', 3);
    },
  });
}
